import React, { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  makeStyles,
  TextField,
  Typography
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import axiosInterceptor from 'src/app/axiosInterceptor';

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined || amount === '') return '0 Vxu';
  return Number(amount).toLocaleString('vi-VN') + ' Vxu';
};

const SUGGESTED_AMOUNTS = [10, 20, 50, 100, 200, 500, 1000, 2000, 5000];

function WalletTopupDialog({ open, onClose }) {
  const classes = useStyles();
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [qrCodeData, setQrCodeData] = useState(null);
  const [error, setError] = useState('');

  const handleAmountChange = (e) => {
    const val = e.target.value.replace(/\D/g, '');
    setAmount(val);
    setError('');
  };

  const handleSuggestClick = (val) => {
    setAmount(String(val));
    setError('');
  };

  const handleGetQRCode = async () => {
    const numAmount = Number(amount);
    if (!amount || numAmount <= 0) {
      setError('Vui lòng nhập số Vxu hợp lệ');
      return;
    }
    if (numAmount < 2) {
      setError('Số Vxu nạp tối thiểu là 2 Vxu');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const response = await axiosInterceptor.post('/QRCode/topup/agency', {
        amount: numAmount
      });
      if (response.data?.success) {
        setQrCodeData(response.data?.payload?.result);
      } else {
        setError(response.data?.message || 'Không thể tạo mã QR nạp Vxu');
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Có lỗi xảy ra khi tạo mã QR');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setAmount('');
    setQrCodeData(null);
    setError('');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleReset}
      maxWidth="xs"
      fullWidth
      disableBackdropClick
    >
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Typography variant="h6" style={{ fontWeight: 600, color: '#ffffff' }}>
          Nạp Vxu vào tài khoản đại lý
        </Typography>
        <IconButton onClick={handleReset} size="small" className={classes.closeButton}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers className={classes.dialogContent}>
        {loading ? (
          <Box display="flex" flexDirection="column" alignItems="center" py={5}>
            <CircularProgress size={40} />
            <Typography variant="body2" style={{ marginTop: 16, color: '#666' }}>
              Đang tạo mã QR nạp Vxu...
            </Typography>
          </Box>
        ) : qrCodeData ? (
          <Box display="flex" flexDirection="column" alignItems="center" py={1}>
            <Typography variant="body2" color="textSecondary" align="center" style={{ marginBottom: 12 }}>
              Vui lòng quét mã QR bằng ứng dụng ngân hàng để chuyển khoản nạp Vxu.
            </Typography>

            {qrCodeData.qrCodeBase64 && (
              <Box className={classes.qrWrapper}>
                <img
                  src={
                    qrCodeData.qrCodeBase64.startsWith('data:')
                      ? qrCodeData.qrCodeBase64
                      : `data:image/png;base64,${qrCodeData.qrCodeBase64}`
                  }
                  alt="QR Code"
                  className={classes.qrImage}
                />
              </Box>
            )}

            <Box width="100%" className={classes.infoBox}>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2" color="textSecondary">Số Vxu:</Typography>
                <Typography variant="body2" style={{ fontWeight: 600, color: '#2e7d32' }}>
                  {formatCurrency(qrCodeData.amount)}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2" color="textSecondary">Nội dung chuyển khoản:</Typography>
                <Typography variant="body2" style={{ fontWeight: 600, color: '#d32f2f' }}>
                  {qrCodeData.payment_code}
                </Typography>
              </Box>
              <Typography variant="caption" color="textSecondary" align="center" display="block" style={{ marginTop: 8, fontStyle: 'italic' }}>
                * Chú ý: Ghi đúng nội dung chuyển khoản để hệ thống tự động cộng Vxu.
              </Typography>
            </Box>
          </Box>
        ) : (
          <Box>
            <InputLabel style={{ marginBottom: 8, fontWeight: 500, color: '#333' }}>
              Số Vxu muốn nạp <span className="text-danger">*</span>
            </InputLabel>
            <TextField
              variant="outlined"
              fullWidth
              size="small"
              placeholder="Nhập số Vxu..."
              value={amount ? Number(amount).toLocaleString('vi-VN') : ''}
              onChange={handleAmountChange}
              error={!!error}
              helperText={error || (amount ? `= ${(Number(amount) * 1000).toLocaleString('vi-VN')} VNĐ` : '')}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography variant="caption" style={{ color: '#888', fontWeight: 600 }}>Vxu</Typography>
                  </InputAdornment>
                )
              }}
              className={classes.textField}
            />

            <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginBottom: 12 }}>
              Gợi ý số Vxu nạp:
            </Typography>

            <Grid container spacing={1}>
              {SUGGESTED_AMOUNTS.map((val) => (
                <Grid item xs={4} key={val}>
                  <Button
                    variant="outlined"
                    fullWidth
                    size="small"
                    className={classes.suggestBtn}
                    onClick={() => handleSuggestClick(val)}
                  >
                    <Box textAlign="center" style={{ lineHeight: 1.3, padding: '2px 0' }}>
                      <Typography style={{ fontSize: 12, fontWeight: 600, color: 'inherit' }}>
                        {Number(val).toLocaleString('vi-VN')} Vxu
                      </Typography>
                      <Typography style={{ fontSize: 10.5, color: '#666', opacity: 0.9 }}>
                        ({(val * 1000).toLocaleString('vi-VN')} vnđ)
                      </Typography>
                    </Box>
                  </Button>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </DialogContent>

      <DialogActions style={{ padding: '12px 24px' }}>
        {!qrCodeData ? (
          <Button
            variant="contained"
            onClick={handleGetQRCode}
            disabled={loading || !amount}
            fullWidth
            style={{
              textTransform: 'none',
              height: 40,
              backgroundColor: loading || !amount ? undefined : '#D9042F',
              color: loading || !amount ? undefined : '#fff'
            }}
          >
            Tạo mã QR nạp Vxu
          </Button>
        ) : (
          <Button
            variant="outlined"
            onClick={() => setQrCodeData(null)}
            fullWidth
            style={{ textTransform: 'none', height: 40 }}
          >
            Nhập số Vxu khác
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 24px',
    backgroundColor: '#D9042F',
    color: '#fff'
  },
  closeButton: {
    color: '#fff'
  },
  dialogContent: {
    padding: '24px'
  },
  textField: {
    marginBottom: '16px',
    '& input': {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333'
    }
  },
  suggestBtn: {
    textTransform: 'none',
    borderColor: '#e0e0e0',
    padding: '4px 2px',
    borderRadius: '8px',
    '&:hover': {
      borderColor: '#D9042F',
      color: '#D9042F',
      backgroundColor: '#fff5f5'
    }
  },
  qrWrapper: {
    border: '1px solid #e0e0e0',
    borderRadius: '12px',
    padding: '16px',
    background: '#fff',
    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: '16px'
  },
  qrImage: {
    width: '200px',
    height: '200px',
    objectFit: 'contain'
  },
  infoBox: {
    background: '#f9f9f9',
    borderRadius: '8px',
    padding: '12px 16px',
    border: '1px solid #eef0f2'
  }
}));

export default WalletTopupDialog;
